import React from 'react';

/**
 * LoadingSpinner 컴포넌트 Props 인터페이스
 * 
 * 로딩 스피너의 크기, 색상, 속도 등을 설정할 수 있습니다.
 */
interface LoadingSpinnerProps {
  size?: number | 'sm' | 'md' | 'lg';
  color?: string | 'white' | 'blue' | 'gray';
  speed?: number;
  className?: string;
}

/**
 * 로딩 스피너 컴포넌트
 * 
 * 애니메이션이 있는 원형 로딩 스피너를 표시합니다.
 * 크기, 색상, 속도를 커스터마이징할 수 있으며,
 * CSS 변수를 사용하여 애니메이션을 제어합니다.
 * 
 * 주요 기능:
 * - 크기별 프리셋 (sm, md, lg) 또는 커스텀 픽셀 값
 * - 색상 프리셋 (white, blue, gray) 또는 커스텀 색상
 * - 애니메이션 속도 조절
 * - CSS 변수를 통한 동적 스타일링
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'gray',
  speed = 2,
  className = ''
}) => {
  /**
   * size를 픽셀 값으로 변환하는 함수
   * 
   * @param size - 크기 값 (프리셋 또는 픽셀)
   * @returns 픽셀 단위 크기
   */
  const getSizeInPx = (size: number | 'sm' | 'md' | 'lg'): number => {
    if (typeof size === 'number') return size;
    switch (size) {
      case 'sm': return 20;
      case 'md': return 48;
      case 'lg': return 64;
      default: return 48;
    }
  };

  /**
   * color를 실제 색상 값으로 변환하는 함수
   * 
   * @param color - 색상 값 (프리셋 또는 커스텀)
   * @returns 실제 색상 값
   */
  const getColorValue = (color: string | 'white' | 'blue' | 'gray'): string => {
    if (color === 'white' || color === 'blue' || color === 'gray') {
      switch (color) {
        case 'white': return 'white';
        case 'blue': return '#2563eb';
        case 'gray': return '#afbac9';
        default: return 'black';
      }
    }
    return color;
  };

  const sizeInPx = getSizeInPx(size);
  const colorValue = getColorValue(color);

  return (
    <svg
      className={`container ${className}`}
      viewBox="0 0 40 40"
      height={sizeInPx}
      width={sizeInPx}
      style={{
        '--uib-size': `${sizeInPx}px`,
        '--uib-color': colorValue,
        '--uib-speed': `${speed}s`
      } as React.CSSProperties}
    >
      {/* 배경 트랙 원 */}
      <circle
        className="track"
        cx="20"
        cy="20"
        r="17.5"
        pathLength="100"
        strokeWidth="4px"
        fill="none"
      />
      {/* 애니메이션되는 원 */}
      <circle
        className="car"
        cx="20"
        cy="20"
        r="17.5"
        pathLength="100"
        strokeWidth="4px"
        fill="none"
      />
    </svg>
  );
};

export { LoadingSpinner };

/**
 * FullScreenLoading 컴포넌트 Props 인터페이스
 * 
 * 전체 화면 로딩 상태를 표시하는 컴포넌트의 설정을 정의합니다.
 */
interface FullScreenLoadingProps {
  message?: string;
  className?: string;
}

/**
 * 전체 화면 로딩 컴포넌트
 * 
 * 페이지 전체를 덮는 로딩 오버레이를 표시합니다.
 * 반투명 검은 배경에 중앙 정렬된 로딩 스피너와 메시지를 보여줍니다.
 * 
 * @param message - 표시할 로딩 메시지
 * @param className - 추가 CSS 클래스
 */
export function FullScreenLoading({
  message = "로딩 중입니다...",
  className = ""
}: FullScreenLoadingProps) {
  return (
    <div className={`min-h-screen bg-black/60 flex items-center justify-center ${className}`}>
      {/* 로딩 콘텐츠 */}
      <div className="text-center">
        <LoadingSpinner size="md" color="white" className="mx-auto mb-4" />
        <p className="mt-4 text-white text-base">{message}</p>
      </div>
    </div>
  );
}

/**
 * InlineLoading 컴포넌트 Props 인터페이스
 * 
 * 인라인 로딩 상태를 표시하는 컴포넌트의 설정을 정의합니다.
 */
interface InlineLoadingProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  color?: 'white' | 'blue' | 'gray';
  className?: string;
}

/**
 * 인라인 로딩 컴포넌트
 * 
 * 컨텐츠 내부에 인라인으로 표시되는 로딩 상태입니다.
 * 로딩 스피너와 선택적 메시지를 가로로 배치합니다.
 * 
 * @param message - 표시할 로딩 메시지 (선택사항)
 * @param size - 로딩 스피너 크기
 * @param color - 로딩 스피너 색상
 * @param className - 추가 CSS 클래스
 */
export function InlineLoading({
  message,
  size = 'md',
  color = 'gray',
  className = ""
}: InlineLoadingProps) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      {/* 로딩 스피너 */}
      <LoadingSpinner size={size} color={color} />
      {/* 로딩 메시지 (있는 경우에만 표시) */}
      {message && (
        <span className="ml-2 text-sm text-gray-600">{message}</span>
      )}
    </div>
  );
} 